/* eslint-disable react/prop-types */
import React, { useMemo } from 'react';
import { Select } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import { Line, Bar } from 'react-chartjs-2';
import { getPopupContainerBody } from '../ShippingManagement/shipmentFieldOptions';

const PIPELINE_SELECT_DROPDOWN = 'modern-dropdown-popup mc-dpr-modal-select-dd heat-profile-select-dropdown';
const PIPELINE_SELECT_CLASS = 'spacer-log-filter-select';
import SpacerLogKpiCard from './SpacerLogKpiCard';
import SpacerLogKpiStrip from './SpacerLogKpiStrip';
import SpacerLogHeatmap from './SpacerLogHeatmap';
import {
  fmtMinP,
  completionRail,
  downtimeRail,
  submitRail,
  PIPELINE_CHART_COLORS,
  CHART_COLORS,
} from './spacerLogUtils';
import { baseChartOptions, gridScales } from './chartSetup';
import './chartSetup';

const SpacerLogOverviewView = ({
  data,
  sowId,
  periodType,
  onSowChange,
  onPeriodTypeChange,
  onPrevPeriod,
  onNextPeriod,
  canNextPeriod,
  onDayClick,
  isLoading = false,
}) => {
  const { meta, filters, kpis, heatmap, trends, pipelineComparison, breakdown } = data || {};

  const kpiCards = useMemo(() => {
    if (!kpis) return null;
    const cr = kpis.completionRatePct ?? 0;
    const crRail = completionRail(cr);
    const subPct = kpis.bothShiftsSubmittedPct ?? 0;
    const subRail = submitRail(subPct);
    const dtRail = downtimeRail(kpis.totalDowntimeMinutes, kpis.activeDays);
    const skipped = kpis.skippedDays ?? 0;

    return (
      <>
        <SpacerLogKpiCard
          label="Joints Completed"
          value={(kpis.jointsCompleted ?? 0).toLocaleString()}
          sub={`${kpis.periodLabel || meta?.periodLabel || ''} · ${kpis.activeDays ?? 0} active days`}
          rail="good"
        />
        <SpacerLogKpiCard
          label="Completion Rate"
          value={`${cr}%`}
          sub="avg across period"
          rail={crRail}
          tag={cr >= 90 ? 'Healthy' : cr >= 75 ? 'Watch' : 'At risk'}
          tagClass={crRail}
        />
        <SpacerLogKpiCard
          label="Total Downtime"
          value={fmtMinP(kpis.totalDowntimeMinutes)}
          sub={`across ${kpis.activeDays ?? 0} active days`}
          rail={dtRail}
        />
        <SpacerLogKpiCard
          label="Avg Cycle Time"
          value={`${kpis.avgCycleTimeMinutes ?? '—'} min`}
          sub="arrival → pull complete"
          rail="neutral"
        />
        <SpacerLogKpiCard
          label="Both Shifts Submitted"
          value={`${subPct}%`}
          sub={`${kpis.bothShiftsSubmittedCount ?? 0}/${kpis.activeDays ?? 0} active days`}
          rail={subRail}
          tag={subRail === 'good' ? 'On track' : 'Chase sign-off'}
          tagClass={subRail}
        />
        <SpacerLogKpiCard
          label="Skipped Days"
          value={skipped}
          sub="no production logged"
          rail={skipped > 3 ? 'warn' : 'good'}
        />
      </>
    );
  }, [kpis, meta]);

  const trendChart = useMemo(() => {
    const points = trends?.dailyCompleted || [];
    if (!points.length) return null;
    return {
      data: {
        labels: points.map((p) => p.date),
        datasets: [
          {
            label: 'Joints completed',
            data: points.map((p) => p.jointsCompleted),
            borderColor: CHART_COLORS.primary,
            backgroundColor: 'rgba(0, 168, 142, 0.12)',
            fill: true,
            tension: 0.25,
            pointRadius: 0,
            borderWidth: 1.5,
          },
          {
            label: '7-day avg',
            data: points.map((p) => p.movingAvg7),
            borderColor: CHART_COLORS.warn,
            borderWidth: 2,
            pointRadius: 0,
            tension: 0.3,
          },
        ],
      },
      options: {
        ...baseChartOptions,
        plugins: {
          legend: { position: 'top', align: 'end', labels: { boxWidth: 10, boxHeight: 10, color: CHART_COLORS.text } },
        },
        scales: gridScales,
      },
    };
  }, [trends]);

  const downtimeChart = useMemo(() => {
    const weeks = trends?.weeklyDowntime || [];
    if (!weeks.length) return null;
    return {
      data: {
        labels: weeks.map((w) => w.weekLabel),
        datasets: [
          {
            data: weeks.map((w) => w.downtimeMinutes),
            backgroundColor: weeks.map((w) =>
              w.downtimeMinutes > 500 ? CHART_COLORS.bad : w.downtimeMinutes > 300 ? CHART_COLORS.orange : CHART_COLORS.warn
            ),
            borderRadius: 3,
            maxBarThickness: 26,
          },
        ],
      },
      options: {
        ...baseChartOptions,
        plugins: { legend: { display: false } },
        scales: gridScales,
      },
    };
  }, [trends]);

  const cycleChart = useMemo(() => {
    const weeks = trends?.weeklyAvgCycle || [];
    if (!weeks.length) return null;
    return {
      data: {
        labels: weeks.map((w) => w.weekLabel),
        datasets: [
          {
            data: weeks.map((w) => w.avgCycleTimeMinutes),
            borderColor: CHART_COLORS.secondary,
            backgroundColor: 'rgba(0, 168, 142, 0.12)',
            fill: true,
            tension: 0.3,
            pointRadius: 2,
            borderWidth: 2,
          },
        ],
      },
      options: {
        ...baseChartOptions,
        plugins: { legend: { display: false } },
        scales: gridScales,
      },
    };
  }, [trends]);

  const pipeChart = useMemo(() => {
    const pipes = pipelineComparison || [];
    if (!pipes.length) return null;
    return {
      data: {
        labels: pipes.map((p) => p.label),
        datasets: [
          {
            data: pipes.map((p) => p.jointsCompleted),
            backgroundColor: pipes.map((_, i) => PIPELINE_CHART_COLORS[i % PIPELINE_CHART_COLORS.length]),
            borderRadius: 3,
            maxBarThickness: 40,
          },
        ],
      },
      options: {
        ...baseChartOptions,
        indexAxis: 'y',
        plugins: { legend: { display: false } },
        scales: {
          x: { grid: { color: CHART_COLORS.grid }, beginAtZero: true },
          y: { grid: { display: false } },
        },
      },
    };
  }, [pipelineComparison]);

  const maxCompleted = Math.max(...(pipelineComparison || []).map((p) => p.jointsCompleted), 1);

  const pipelineOptions = useMemo(() => {
    const items = (filters?.pipelines || []).map((p) => ({
      value: p.sowId,
      label: p.label,
    }));
    return [{ value: 'all', label: 'All pipelines (SOW)' }, ...items];
  }, [filters?.pipelines]);

  const pipelineValue = sowId === 'all' ? 'all' : Number(sowId);

  return (
    <div>
      <div className="spacer-log-dashboard__filterbar">
        <div className="spacer-log-dashboard__fb-group spacer-log-dashboard__fb-group--pipeline">
          <span className="spacer-log-dashboard__fb-label">Pipeline</span>
          <Select
            className={PIPELINE_SELECT_CLASS}
            dropdownClassName={PIPELINE_SELECT_DROPDOWN}
            getPopupContainer={getPopupContainerBody}
            suffixIcon={<DownOutlined />}
            value={pipelineValue}
            options={pipelineOptions}
            onChange={onSowChange}
            placeholder="Select pipeline"
          />
        </div>
        <div className="spacer-log-dashboard__period-toggle">
          {['month', 'quarter', 'year'].map((p) => (
            <button
              key={p}
              type="button"
              className={`spacer-log-dashboard__period-btn${periodType === p ? ' spacer-log-dashboard__period-btn--active' : ''}`}
              onClick={() => onPeriodTypeChange(p)}
            >
              {p.charAt(0).toUpperCase() + p.slice(1)}
            </button>
          ))}
        </div>
        <div className="spacer-log-dashboard__period-nav">
          <button type="button" className="spacer-log-dashboard__nav-btn" onClick={onPrevPeriod}>
            ‹
          </button>
          <span className="spacer-log-dashboard__period-label">{meta?.periodLabel || '—'}</span>
          <button
            type="button"
            className="spacer-log-dashboard__nav-btn"
            onClick={onNextPeriod}
            disabled={!canNextPeriod}
          >
            ›
          </button>
        </div>
      </div>

      {kpiCards || isLoading ? (
        <SpacerLogKpiStrip isLoading={isLoading && !kpis}>{kpiCards}</SpacerLogKpiStrip>
      ) : null}

      <div className="spacer-log-panel">
        <div className="spacer-log-panel__head">
          <span className="spacer-log-panel__title">Daily Completion — Calendar Heatmap</span>
          <span className="spacer-log-panel__note">colour = completion rate</span>
        </div>
        <SpacerLogHeatmap
          days={heatmap?.days}
          fromDate={meta?.fromDate}
          toDate={meta?.toDate}
          onDayClick={onDayClick}
        />
      </div>

      <div className="spacer-log-row2">
        <div className="spacer-log-panel">
          <div className="spacer-log-panel__head">
            <span className="spacer-log-panel__title">Joints Completed — Daily Trend</span>
            <span className="spacer-log-panel__note">7-day moving average overlaid</span>
          </div>
          <div className="spacer-log-chart-box spacer-log-chart-box--tall">
            {trendChart ? <Line data={trendChart.data} options={trendChart.options} /> : null}
          </div>
        </div>
        <div className="spacer-log-panel">
          <div className="spacer-log-panel__head">
            <span className="spacer-log-panel__title">Downtime — Weekly Total</span>
            <span className="spacer-log-panel__note">
              {(trends?.weeklyDowntime || []).length} weeks in period
            </span>
          </div>
          <div className="spacer-log-chart-box spacer-log-chart-box--tall">
            {downtimeChart ? <Bar data={downtimeChart.data} options={downtimeChart.options} /> : null}
          </div>
        </div>
      </div>

      <div className="spacer-log-row2">
        <div className="spacer-log-panel">
          <div className="spacer-log-panel__head">
            <span className="spacer-log-panel__title">Avg Cycle Time — Weekly</span>
            <span className="spacer-log-panel__note">minutes, arrival → pull complete</span>
          </div>
          <div className="spacer-log-chart-box spacer-log-chart-box--tall">
            {cycleChart ? <Line data={cycleChart.data} options={cycleChart.options} /> : null}
          </div>
        </div>
        <div className="spacer-log-panel">
          <div className="spacer-log-panel__head">
            <span className="spacer-log-panel__title">Pipeline Comparison</span>
            <span className="spacer-log-panel__note">joints completed, this period</span>
          </div>
          <div className="spacer-log-chart-box spacer-log-chart-box--short">
            {pipeChart && sowId === 'all' ? <Bar data={pipeChart.data} options={pipeChart.options} /> : null}
            {sowId !== 'all' ? (
              <p className="spacer-log-cell-muted" style={{ padding: '8px 0' }}>
                Select all pipelines to compare SOW performance.
              </p>
            ) : null}
          </div>
          {sowId === 'all' && (pipelineComparison || []).length > 0 ? (
            <div className="spacer-log-pipe-compare">
              {(pipelineComparison || []).map((s, i) => (
                <div key={s.sowId} className="spacer-log-pipe-row">
                  <span className="spacer-log-pipe-name">{s.label}</span>
                  <div className="spacer-log-pipe-bar-track">
                    <div
                      className="spacer-log-pipe-bar-fill"
                      style={{
                        width: `${((s.jointsCompleted / maxCompleted) * 100).toFixed(0)}%`,
                        background: PIPELINE_CHART_COLORS[i % PIPELINE_CHART_COLORS.length],
                      }}
                    />
                  </div>
                  <span className="spacer-log-pipe-stat">{fmtMinP(s.downtimeMinutes)}</span>
                  <span className="spacer-log-pipe-stat">{s.avgCycleTimeMinutes} min</span>
                </div>
              ))}
              <div className="spacer-log-pipe-row" style={{ opacity: 0.6 }}>
                <span className="spacer-log-pipe-name" />
                <span />
                <span className="spacer-log-pipe-stat" style={{ fontSize: 9, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                  downtime
                </span>
                <span className="spacer-log-pipe-stat" style={{ fontSize: 9, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                  cycle
                </span>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <div className="spacer-log-panel">
        <div className="spacer-log-panel__head">
          <span className="spacer-log-panel__title">{breakdown?.title || 'Period Breakdown'}</span>
          <span className="spacer-log-panel__note">weekly / monthly rollups from server</span>
        </div>
        <div className="spacer-log-table-wrap spacer-log-scroll-table">
          <table className="spacer-log-table">
            <thead>
              <tr>
                <th>{breakdown?.bucketLabel || 'Period'}</th>
                <th>Joints Completed</th>
                <th>Completion Rate</th>
                <th>Downtime</th>
                <th>Avg Cycle</th>
                <th>Days Active</th>
                <th>Days Skipped</th>
              </tr>
            </thead>
            <tbody>
              {(breakdown?.rows || []).map((row) => {
                const pill = completionRail(row.completionRatePct);
                return (
                  <tr key={row.bucketKey || row.label}>
                    <td>{row.label}</td>
                    <td>{row.jointsCompleted}</td>
                    <td>
                      <span className={`spacer-log-pill spacer-log-pill--${pill}`}>{row.completionRatePct}%</span>
                    </td>
                    <td>{fmtMinP(row.downtimeMinutes)}</td>
                    <td>
                      {row.avgCycleTimeMinutes != null ? (
                        `${row.avgCycleTimeMinutes} min`
                      ) : (
                        <span className="spacer-log-cell-muted">—</span>
                      )}
                    </td>
                    <td>{row.activeDays}</td>
                    <td>{row.skippedDays || <span className="spacer-log-cell-muted">0</span>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SpacerLogOverviewView;
